import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Divider,
    TextField,
    IconButton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AutocompleteSelect from '../../../../components/atom/AutoCompleteSelect';
import { freeTicketSeatsAutocomplete, ticketSeatsAutocomplete } from '../../../../autocomplete';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CentInput from '../../../../components/atom/CentInput';
import { getTicketValidationWarnings, updatePriceFromMatrix } from './ticketValidationHelpers';
import { formatCentsToUsd } from '../../../../helpers/formatCentsToUsd';
import { previewItemFinalPriceCents } from '../../../../helpers/calculateShowFees';

function OneTicketEdit({
    showId,
    showFees,
    isOnline,
    timeslotId,
    values,
    setFieldValue,
    isSubmitting,
    errors = {},
    removeAddedTicket,
    bookingId,
    updatedData,
    allTicketTypes,
    allTicketOptions,
    priceOverrides,
    isTicketCombinationAllowed = () => true,
    limitedModification,
    onAutoSeat,
    canModifySeating = true,
    canCrossTicketType = false,
    excludedSeatIds = [],
    netRate = false,
    commissionMatrix = {},
}) {
    const { t } = useTranslation();

    const [automaticSeating, _setAutomaticSeating] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [validationWarnings, setValidationWarnings] = useState({
        seatMismatch: false,
        invalidCombination: false,
        messages: [],
    });

    // Get current ticket type and option name
    const currentTicketType = allTicketTypes?.find((type) => type.id === values?.ticketTypeId);
    const currentTicketOptionName = values?.ticketOptionName;
    const allowedOptionsForSelectedType = allTicketOptions?.filter((option) => {
        if (!values?.ticketTypeId) {
            return true;
        }
        return isTicketCombinationAllowed(values.ticketTypeId, option.id);
    });

    // Validate warnings when seat or type/option changes
    useEffect(() => {
        if (!values?.ticketTypeId || !values?.ticketOptionId) {
            setValidationWarnings({ seatMismatch: false, invalidCombination: false, messages: [] });
            return;
        }

        // Get validation warnings
        const warnings = getTicketValidationWarnings(
            values,
            currentTicketType,
            currentTicketOptionName,
            t,
        );
        setValidationWarnings(warnings);
    }, [
        values?.ticketTypeId,
        values?.ticketOptionId,
        values?.ticketSeatId,
        currentTicketType,
        currentTicketOptionName,
    ]);

    const finalPrice = previewItemFinalPriceCents(values, {
        netRate,
        commissionMatrix,
        showFees,
        isOnline,
    });

    return (
        <Paper
            elevation={2}
            sx={{
                mb: 1,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                opacity: values?.shouldDelete ? 0.4 : 1,
                transition: 'all 0.2s',
                overflow: 'hidden',
                '&:hover': {
                    boxShadow: 4,
                    borderColor: 'primary.main',
                },
            }}
        >
            {/* Main row - compact view */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' } }}>
                {/* Seat section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        px: 2,
                        py: 1,
                        minWidth: { xs: 'auto', lg: 150 },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                        {!values?.ticketSeatId &&
                            values?.ticketTypeId &&
                            onAutoSeat &&
                            !values?.shouldDelete && (
                                <Tooltip title={t('bookings.autoSeatOne')}>
                                    <IconButton
                                        size="small"
                                        sx={{ color: 'white' }}
                                        onClick={() => onAutoSeat(values)}
                                        disabled={isSubmitting}
                                    >
                                        <AutoFixHighIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        {(() => {
                            const seatFieldSx = {
                                '& .MuiInputBase-root': { color: 'white' },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.3)',
                                },
                                '& .MuiFormLabel-root': { color: 'white' },
                            };
                            return canModifySeating ? (
                                <AutocompleteSelect
                                    fullWidth
                                    initCall={ticketSeatsAutocomplete.init}
                                    autocompleteCall={freeTicketSeatsAutocomplete.search}
                                    value={values?.ticketSeatId}
                                    setValue={(ev, mapper) => {
                                        setFieldValue('ticketSeatId', ev);
                                        setFieldValue('ticketSeatName', mapper(ev)?.name || null);
                                    }}
                                    searchParams={{
                                        showId,
                                        timeslotId,
                                        ticketTypeId: canCrossTicketType
                                            ? null
                                            : values?.ticketTypeId || null,
                                        bookingId: bookingId || null,
                                        excludedSeatIds,
                                    }}
                                    disabled={isSubmitting}
                                >
                                    <TextField
                                        label={t('bookingitems.columns.ticketSeatName')}
                                        size="small"
                                        sx={{
                                            ...seatFieldSx,
                                            '& .MuiSvgIcon-root': { color: 'white' },
                                        }}
                                        endAdornment={
                                            automaticSeating && (
                                                <Tooltip
                                                    title={t(
                                                        `tickettypes.automaticSeating.${automaticSeating}`,
                                                    )}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'white' }}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }
                                    />
                                </AutocompleteSelect>
                            ) : (
                                <TextField
                                    fullWidth
                                    label={t('bookingitems.columns.ticketSeatName')}
                                    value={values?.ticketSeatName || ''}
                                    size="small"
                                    disabled
                                    sx={{
                                        ...seatFieldSx,
                                        '& .MuiInputBase-input.Mui-disabled': {
                                            WebkitTextFillColor: 'rgba(255,255,255,0.7)',
                                        },
                                        '& .MuiFormLabel-root.Mui-disabled': {
                                            color: 'rgba(255,255,255,0.5)',
                                        },
                                    }}
                                />
                            );
                        })()}
                    </Box>
                </Box>
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: 'none', lg: 'block' } }}
                />
                {/* Middle - Option and Type */}
                <Box
                    sx={{
                        flex: 1,
                        p: 1,
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        gap: 1,
                        alignItems: { xs: 'stretch', lg: 'center' },
                    }}
                >
                    <ConfirmationNumberIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>{t('bookingitems.columns.ticketTypeName')}</InputLabel>
                            <Select
                                value={values?.ticketTypeId || ''}
                                onChange={(ev) => {
                                    const selectedType = allTicketTypes?.find(
                                        (type) => type.id === ev.target.value,
                                    );
                                    setFieldValue('ticketTypeId', ev.target.value);
                                    setFieldValue('ticketTypeName', selectedType?.name || null);
                                    if (
                                        values?.ticketOptionId &&
                                        !isTicketCombinationAllowed(
                                            ev.target.value,
                                            values.ticketOptionId,
                                        )
                                    ) {
                                        setFieldValue('ticketOptionId', '');
                                        setFieldValue('ticketOptionName', '');
                                    }
                                    // Update price when type changes
                                    updatePriceFromMatrix(
                                        ev.target.value,
                                        values?.ticketOptionName,
                                        allTicketTypes,
                                        setFieldValue,
                                        values?.ticketOptionId,
                                        priceOverrides,
                                    );
                                }}
                                disabled={isSubmitting || limitedModification}
                                label={t('bookingitems.columns.ticketTypeName')}
                            >
                                {allTicketTypes?.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <FormControl fullWidth size="small" required>
                            <InputLabel>{t('bookingitems.columns.ticketOptionName')}</InputLabel>
                            <Select
                                value={values?.ticketOptionId || ''}
                                onChange={(ev) => {
                                    const selectedOption = allTicketOptions?.find(
                                        (opt) => opt.id === ev.target.value,
                                    );
                                    const optionName = selectedOption?.name || null;
                                    setFieldValue('ticketOptionId', ev.target.value);
                                    setFieldValue('ticketOptionName', optionName);
                                    // Update price when option changes
                                    updatePriceFromMatrix(
                                        values?.ticketTypeId,
                                        optionName,
                                        allTicketTypes,
                                        setFieldValue,
                                        ev.target.value,
                                        priceOverrides,
                                    );
                                }}
                                disabled={isSubmitting || limitedModification}
                                label={t('bookingitems.columns.ticketOptionName')}
                            >
                                {allowedOptionsForSelectedType?.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Right - Price and actions */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                        gap: 1,
                        py: { xs: 1, lg: 0 },
                        bgcolor: 'grey.50',
                        px: 1,
                        borderLeft: { xs: 0, lg: '2px dashed' },
                        borderTop: { xs: '2px dashed', lg: 0 },
                        borderColor: 'divider',
                    }}
                >
                    <CentInput
                        value={values.customPriceCents}
                        onChange={(ev) => {
                            setFieldValue('customPriceCents', ev.target.value);
                        }}
                        error={
                            values.ticketOptionId && values.ticketTypeId && Boolean(errors.price)
                        }
                        size="small"
                        label={t('bookingitems.columns.price')}
                        sx={{ width: 100 }}
                        disabled={limitedModification}
                    />
                    <TextField
                        label={t('bookingitems.columns.priceWithTaxAndFees')}
                        disabled
                        value={formatCentsToUsd(finalPrice)}
                        sx={{ width: 100 }}
                    />
                    <Tooltip title={t('common.expandNotes')}>
                        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {!limitedModification &&
                        (typeof values?.oldId === 'number' ? (
                            <Tooltip title={t('common.delete')}>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        setFieldValue('shouldDelete', !values.shouldDelete)
                                    }
                                >
                                    <DeleteIcon
                                        fontSize="small"
                                        color={values.shouldDelete ? 'error' : 'inherit'}
                                    />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title={t('common.delete')}>
                                <IconButton size="small" onClick={removeAddedTicket}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ))}
                </Box>
            </Box>

            {/* Validation warnings */}
            {(validationWarnings.seatMismatch || validationWarnings.messages.length > 0) && (
                <Box sx={{ px: 2, pt: 1 }}>
                    {validationWarnings.seatMismatch && (
                        <Alert
                            severity="error"
                            icon={<WarningIcon fontSize="inherit" />}
                            sx={{ mb: 1 }}
                        >
                            {t('bookingitems.validation.seatNotCompatible')}
                        </Alert>
                    )}
                    {validationWarnings.messages.map((message, index) => (
                        <Alert
                            key={index}
                            severity="warning"
                            icon={<WarningIcon fontSize="inherit" />}
                            sx={{ mb: 1 }}
                        >
                            {message}
                        </Alert>
                    ))}
                </Box>
            )}

            {/* Expanded section - Notes */}
            {expanded && (
                <Box
                    sx={{
                        p: 1,
                        pt: 0,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                    }}
                >
                    <Box
                        sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1 }}
                    >
                        <TextField
                            fullWidth
                            label={t('bookingitems.columns.note')}
                            value={values.note}
                            onChange={(ev) => setFieldValue('note', ev.target.value)}
                            disabled={isSubmitting}
                            size="small"
                            multiline
                            rows={2}
                        />
                        <TextField
                            fullWidth
                            label={t('bookingitems.columns.checkinNote')}
                            value={values.checkinNote}
                            onChange={(ev) => setFieldValue('checkinNote', ev.target.value)}
                            disabled={isSubmitting}
                            size="small"
                            multiline
                            rows={2}
                        />
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

export default OneTicketEdit;
